import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Form, ListGroup, Modal, Spinner } from 'react-bootstrap';
import { FaComments, FaPaperPlane, FaTrash, FaLock } from 'react-icons/fa';
import moment from 'moment';
import Swal from 'sweetalert2';
import { agregarOtNota, eliminarOtNota, getOtNotas } from '../../api/common';

// Muestra todas las notas de una OT y permite agregar o eliminar las de tipo
// 'manual'. Las notas tipo 'cierre' o 'cancelacion' son inmutables (sin botón
// de eliminar) y aparecen etiquetadas con un badge para que el usuario entienda
// que fueron generadas por una acción del sistema sobre la OT.
//
// La `observacion` legacy (campo string previo a esta feature) se muestra como
// una pseudo-nota al final del listado, también sin botón de eliminar.
const OtNotasModal = ({ show, onHide, otId, numeroOrden, onChanged }) => {
  const [notas, setNotas] = useState([]);
  const [observacionLegacy, setObservacionLegacy] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const refresh = useCallback(async () => {
    if (!otId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await getOtNotas(otId);
      const payload = res?.data?.data ?? res?.data ?? {};
      setNotas(Array.isArray(payload.notas) ? payload.notas : []);
      setObservacionLegacy(payload.observacion || '');
    } catch (err) {
      console.error('Error listando notas:', err);
      setError('No fue posible cargar las notas.');
    } finally {
      setLoading(false);
    }
  }, [otId]);

  useEffect(() => {
    if (show) {
      refresh();
    } else {
      setDescripcion('');
      setError(null);
    }
  }, [show, refresh]);

  const handleAdd = async (event) => {
    event.preventDefault();
    const value = descripcion.trim();
    if (!value) return;
    setSaving(true);
    setError(null);
    try {
      const res = await agregarOtNota(otId, value);
      const payload = res?.data?.data ?? res?.data ?? {};
      setNotas(Array.isArray(payload.notas) ? payload.notas : []);
      setDescripcion('');
      if (onChanged) onChanged();
    } catch (err) {
      console.error('Error agregando nota:', err);
      const msg = err?.response?.data?.error?.message || err?.response?.data?.detail || 'No fue posible agregar la nota.';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (nota) => {
    const confirm = await Swal.fire({
      title: '¿Eliminar nota?',
      text: 'Esta acción no se puede deshacer.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Eliminar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#d33'
    });
    if (!confirm.isConfirmed) return;

    setDeletingId(nota._id);
    setError(null);
    try {
      const res = await eliminarOtNota(otId, nota._id);
      const payload = res?.data?.data ?? res?.data ?? {};
      setNotas(Array.isArray(payload.notas) ? payload.notas : []);
      if (onChanged) onChanged();
    } catch (err) {
      console.error('Error eliminando nota:', err);
      const msg = err?.response?.data?.error?.message || err?.response?.data?.detail || 'No fue posible eliminar la nota.';
      setError(msg);
    } finally {
      setDeletingId(null);
    }
  };

  const sortedDesc = [...notas].sort(
    (a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime()
  );

  const totalItems = sortedDesc.length + (observacionLegacy ? 1 : 0);

  const tipoBadge = (tipo) => {
    if (tipo === 'cierre') return <Badge bg="danger" className="ms-2">Cierre</Badge>;
    if (tipo === 'cancelacion') return <Badge bg="warning" text="dark" className="ms-2">Cancelación</Badge>;
    return null;
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>
          <FaComments className="me-2" />
          Notas de la OT {numeroOrden ? <span className="text-muted fs-6">#{numeroOrden}</span> : null}
          <span className="text-muted fs-6 ms-2">({totalItems})</span>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <div className="mb-3">
          <Form.Label className="fw-semibold">Agregar nota</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            placeholder="Escribe una observación sobre esta OT..."
            value={descripcion}
            onChange={(event) => setDescripcion(event.target.value)}
            maxLength={2000}
            disabled={saving}
          />
          <div className="d-flex justify-content-between align-items-center mt-1">
            <small className="text-muted">{descripcion.length}/2000</small>
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={handleAdd}
              disabled={saving || !descripcion.trim()}
            >
              {saving ? (
                <>
                  <Spinner size="sm" animation="border" className="me-1" />
                  Guardando...
                </>
              ) : (
                <>
                  <FaPaperPlane className="me-1" />
                  Agregar
                </>
              )}
            </Button>
          </div>
        </div>

        <hr />

        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" />
            <div className="text-muted mt-2">Cargando notas...</div>
          </div>
        ) : totalItems === 0 ? (
          <p className="text-muted text-center my-4 mb-0">Aún no hay notas para esta OT.</p>
        ) : (
          <ListGroup variant="flush">
            {sortedDesc.map((nota) => {
              const esInmutable = nota.tipo && nota.tipo !== 'manual';
              return (
                <ListGroup.Item
                  key={nota._id || `${nota.fecha}-${nota.usuarioId}`}
                  className="d-flex justify-content-between align-items-start gap-2"
                >
                  <div className="flex-grow-1">
                    <div style={{ whiteSpace: 'pre-wrap' }}>{nota.descripcion}</div>
                    <small className="text-muted d-block mt-1">
                      <strong>{nota.usuarioNombre}</strong>
                      {' · '}
                      {nota.fecha ? moment(nota.fecha).format('DD/MM/YYYY HH:mm') : '-'}
                      {tipoBadge(nota.tipo)}
                    </small>
                  </div>
                  {esInmutable ? (
                    <span
                      className="text-muted"
                      title="Nota generada por el sistema — no se puede eliminar"
                    >
                      <FaLock />
                    </span>
                  ) : nota._id ? (
                    <Button
                      variant="link"
                      size="sm"
                      className="text-danger p-0"
                      onClick={() => handleDelete(nota)}
                      disabled={deletingId === nota._id}
                      aria-label="Eliminar nota"
                    >
                      {deletingId === nota._id ? <Spinner size="sm" animation="border" /> : <FaTrash />}
                    </Button>
                  ) : null}
                </ListGroup.Item>
              );
            })}

            {observacionLegacy && (
              <ListGroup.Item className="d-flex justify-content-between align-items-start gap-2 bg-light">
                <div className="flex-grow-1">
                  <div style={{ whiteSpace: 'pre-wrap' }}>{observacionLegacy}</div>
                  <small className="text-muted d-block mt-1">
                    <strong>Observación histórica</strong>
                    <Badge bg="secondary" className="ms-2">Legacy</Badge>
                  </small>
                </div>
                <span className="text-muted" title="Observación previa migrada — no se puede eliminar">
                  <FaLock />
                </span>
              </ListGroup.Item>
            )}
          </ListGroup>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide}>
          Cerrar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default OtNotasModal;
